import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  X,
  UserRound,
  Landmark,
  Phone,
  Briefcase,
  Building2,
  CreditCard,
  User,
  Hash,
} from "lucide-react";
import { showWarningToast } from "../../../../components/ConfirmDialog";

const INPUT_CLASS =
  "w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-medium text-gray-800 outline-none transition-all placeholder:text-gray-300 focus:border-red-400 focus:ring-2 focus:ring-red-100";

const FloatLabel = ({ label, required, children }) => (
  <div className="relative">
    <label className="absolute -top-2 left-3 z-10 bg-white px-1 text-[10px] font-bold uppercase tracking-wide text-gray-400">
      {label}
      {required ? <span className="text-red-500"> *</span> : null}
    </label>
    {children}
  </div>
);

const Section = ({ icon: Icon, title, subTitle, children }) => (
  <div className="space-y-4 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
    <div className="flex items-center gap-3 border-b border-gray-50 pb-2">
      <div className="rounded-lg bg-red-50 p-2">
        <Icon className="h-5 w-5 text-red-600" />
      </div>
      <div>
        <h3 className="text-sm font-bold uppercase tracking-tight text-gray-800">{title}</h3>
        {subTitle ? <p className="text-xs text-gray-400">{subTitle}</p> : null}
      </div>
    </div>
    <div className="space-y-4 pt-1">{children}</div>
  </div>
);

export default function ContactRepresentativeModal({ open, onClose, onSave, defaultValue = null }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    position: "",
    bank_name: "",
    bank_branch: "",
    bank_account_name: "",
    bank_account_number: "",
  });

  useEffect(() => {
    if (defaultValue) {
      setForm(defaultValue);
    } else {
      setForm({
        name: "",
        phone: "",
        position: "",
        bank_name: "",
        bank_branch: "",
        bank_account_name: "",
        bank_account_number: "",
      });
    }
  }, [defaultValue, open]);

  if (!open) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    if (!form.name?.trim() || !form.phone?.trim()) {
      showWarningToast("Vui lòng nhập họ tên và số điện thoại.");
      return;
    }
    if (!form.position?.trim()) {
      showWarningToast("Vui lòng nhập chức vụ.");
      return;
    }

    onSave(form);
    onClose();
  };

  const isEdit = Boolean(defaultValue);

  return createPortal(
    <div className="fixed inset-0 z-[220]" role="presentation">
      <div
        className="absolute inset-0 bg-black/45 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="absolute inset-0 flex items-end justify-center p-0 pointer-events-none sm:items-center sm:p-4">
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ type: "spring", damping: 30, stiffness: 320 }}
          className="pointer-events-auto relative z-[221] flex h-[90dvh] w-full max-w-3xl flex-col overflow-hidden rounded-t-[1.75rem] border border-gray-200 bg-white shadow-2xl sm:h-auto sm:max-h-[90vh] sm:rounded-[2rem]"
          onClick={(e) => e.stopPropagation()}
        >
        {/* Header */}
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 bg-gradient-to-r from-red-50/70 via-white to-white px-5 py-4 sm:px-6">
          <div className="flex min-w-0 items-center gap-3 sm:gap-4">
            <div className="rounded-2xl border border-red-700/10 bg-red-700/5 p-3 text-red-700 shadow-sm">
              <UserRound className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2.25} />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-black uppercase tracking-tight text-gray-800 sm:text-xl">
                {isEdit ? "Chỉnh sửa" : "Thêm"}{" "}
                <span className="text-red-700">người liên hệ</span>
              </h2>
              <p className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400">
                Đầu mối làm việc và thông tin thanh toán
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-gray-200 bg-white p-2 text-gray-500 transition hover:border-red-200 hover:bg-red-50 hover:text-red-600"
            aria-label="Đóng"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <div className="grid grid-cols-1 gap-5 p-5 sm:gap-6 sm:p-6 md:grid-cols-2">
          <Section icon={UserRound} title="Thông tin cá nhân" subTitle="Tên, SĐT và vai trò liên hệ">
            <FloatLabel label="Họ và tên" required>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Nguyễn Văn A"
                className={INPUT_CLASS}
                autoComplete="name"
              />
            </FloatLabel>

            <FloatLabel label="Số điện thoại" required>
              <div className="relative">
                <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="0xxx xxx xxx"
                  className={`${INPUT_CLASS} pl-10`}
                  inputMode="tel"
                  autoComplete="tel"
                />
              </div>
            </FloatLabel>

            <FloatLabel label="Chức vụ" required>
              <div className="relative">
                <Briefcase className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  name="position"
                  value={form.position}
                  onChange={handleChange}
                  placeholder="VD: Hiệu trưởng, Kế toán..."
                  className={`${INPUT_CLASS} pl-10`}
                />
              </div>
            </FloatLabel>
          </Section>

          <Section icon={Landmark} title="Thông tin ngân hàng" subTitle="Dùng cho hợp đồng & thanh toán (tuỳ chọn)">
            <FloatLabel label="Tên ngân hàng">
              <div className="relative">
                <Building2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  name="bank_name"
                  value={form.bank_name}
                  onChange={handleChange}
                  placeholder="VD: Vietcombank"
                  className={`${INPUT_CLASS} pl-10`}
                />
              </div>
            </FloatLabel>

            <FloatLabel label="Chi nhánh">
              <input
                name="bank_branch"
                value={form.bank_branch}
                onChange={handleChange}
                placeholder="Chi nhánh / PGD"
                className={INPUT_CLASS}
              />
            </FloatLabel>

            <FloatLabel label="Chủ tài khoản">
              <div className="relative">
                <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  name="bank_account_name"
                  value={form.bank_account_name}
                  onChange={handleChange}
                  placeholder="Tên chủ TK"
                  className={`${INPUT_CLASS} pl-10`}
                />
              </div>
            </FloatLabel>

            <FloatLabel label="Số tài khoản">
              <div className="relative">
                <Hash className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  name="bank_account_number"
                  value={form.bank_account_number}
                  onChange={handleChange}
                  placeholder="Số TK ngân hàng"
                  className={`${INPUT_CLASS} pl-10 font-mono text-sm tracking-wide`}
                />
              </div>
            </FloatLabel>
          </Section>
        </div>
        </div>

        {/* Footer */}
        <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-gray-100 bg-gray-50/80 px-5 py-4 sm:flex-row sm:justify-end sm:gap-3 sm:px-6">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-gray-200 bg-white px-6 py-2.5 text-xs font-bold uppercase tracking-wider text-gray-600 transition hover:border-gray-300 hover:bg-gray-50"
          >
            Hủy
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-red-700 to-red-800 px-6 py-2.5 text-xs font-black uppercase tracking-wider text-white shadow-md shadow-red-100 transition hover:shadow-lg"
          >
            <CreditCard className="h-4 w-4 opacity-90" />
            Lưu người liên hệ
          </button>
        </div>
        </motion.div>
      </div>
    </div>,
    document.body
  );
}
